import React, { useRef, useState } from 'react';
import { formatStepError } from './stepError';
import { getFileNameFromUrl } from './fileName';
import type { DoctorDocument } from '../../../types/doctorOnboarding';

type Props = {
  entries: DoctorDocument[];
  isMedicalLicenseRequired: boolean;
  onUpload: (
    file: File,
    documentType: string,
    documentSubType?: string,
    documentNumber?: string
  ) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onNext: () => void;
  onBack: () => void;
};

const DOCUMENT_TYPES = [
  { value: 'government_id', label: 'Government ID' },
  { value: 'medical_license', label: 'Medical License' },
  { value: 'address_proof', label: 'Address Proof' },
  { value: 'other', label: 'Other' },
];

const getDocumentTypeLabel = (type: string) => {
  const match = DOCUMENT_TYPES.find((t) => t.value === type);
  if (match) return match.label;
  return type
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

export default function DocumentsStep({
  entries,
  isMedicalLicenseRequired,
  onUpload,
  onDelete,
  onNext,
  onBack,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [documentType, setDocumentType] = useState('government_id');
  const [uploadingType, setUploadingType] = useState<string | null>(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const requiredTypes = ['government_id', 'address_proof'];
  if (isMedicalLicenseRequired) {
    requiredTypes.push('medical_license');
  }
  const uploadedTypes = new Set(entries.map((d) => d.document_type));
  const missingRequired = requiredTypes.filter((t) => !uploadedTypes.has(t));

  const openFilePicker = (type: string) => {
    setDocumentType(type);
    setError(null);
    window.setTimeout(() => fileInputRef.current?.click(), 0);
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const typeToUpload = documentType;
    setUploadingType(typeToUpload);
    setError(null);
    try {
      await onUpload(file, typeToUpload, undefined, undefined);
      setFileInputKey((k) => k + 1);
    } catch (err: any) {
      setError(formatStepError(err, 'Upload failed'));
    } finally {
      setUploadingType(null);
    }
  };

  return (
    <div>
      <div className="onboarding-section">
        <h3>Document Checklist</h3>
        <div className="document-checklist">
          {DOCUMENT_TYPES.map((t) => {
            const isRequired = requiredTypes.includes(t.value);
            const isUploaded = uploadedTypes.has(t.value);
            const helperText =
              !isRequired && t.value === 'medical_license'
                ? 'Optional for your specialization'
                : null;

            return (
              <div
                key={t.value}
                className={`document-checklist-row ${isUploaded ? 'uploaded' : ''}`}
              >
                <div className="document-checklist-meta">
                  <div className="document-checklist-title">{getDocumentTypeLabel(t.value)}</div>
                  {helperText ? (
                    <div className="document-checklist-helper">{helperText}</div>
                  ) : (
                    <div
                      className={`document-checklist-tag ${
                        isRequired ? 'required' : 'optional'
                      }`}
                    >
                      {isRequired ? 'Required' : 'Optional'}
                    </div>
                  )}
                </div>
                <div className="document-checklist-actions">
                  {isUploaded ? (
                    <span className="document-checklist-status">Uploaded</span>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => openFilePicker(t.value)}
                      disabled={uploadingType !== null}
                    >
                      {uploadingType === t.value ? 'Uploading...' : 'Upload'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <input
          ref={fileInputRef}
          key={fileInputKey}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelected}
          style={{ display: 'none' }}
          aria-hidden
        />
        {error && <div className="onboarding-error">{error}</div>}
      </div>

      <div className="onboarding-section">
        <h3>Uploaded Documents</h3>
        {entries.length === 0 && <p className="onboarding-step-label">No documents uploaded.</p>}
        {entries.map((d) => (
          <div key={d.id} className="onboarding-list-item">
            <div className="onboarding-list-meta">
              <div>
                <strong>{getDocumentTypeLabel(d.document_type)}</strong>
                {d.document_subtype ? ` (${d.document_subtype})` : ''}
              </div>
              {d.document_subtype ? (
                <div className="onboarding-step-label">Subtype: {d.document_subtype}</div>
              ) : null}
              {d.document_number ? (
                <div className="onboarding-step-label">Document No: {d.document_number}</div>
              ) : null}
              {getFileNameFromUrl(d.document_url) && (
                <div className="onboarding-step-label onboarding-file-name">
                  File: {getFileNameFromUrl(d.document_url)}
                </div>
              )}
            </div>
            <button type="button" className="btn-secondary" onClick={() => onDelete(d.id)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={onNext}
          disabled={missingRequired.length > 0}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
